import { createSelector } from '@app/utils/createSelector'

import { createChatServerSelectors } from '@app/packages/chat/selectors'

import { profileChatUidSelector } from '@app/store/selectors/profile'

import { chatStateSelector } from './slice'

/** The package selector suite bound to the mounted `chat` slice; its `chatServer*` keys are re-exported unprefixed. */
export const {
  chatServerChannelsSelector: chatChannelsSelector,
  chatServerParticipantsSelector: chatParticipantsSelector,
  chatServerMessagesSelector: chatMessagesSelector,
  chatServerResolvedSelector: chatResolvedSelector,
  chatServerGlobalPresenceSelector: chatGlobalPresenceSelector,
  chatServerTypingSelector: chatTypingSelector,
  chatServerUnreadSelector: chatUnreadSelector,
  chatServerConnectedSelector: chatConnectedSelector,
  chatServerReadySelector: chatReadySelector,
  chatServerChannelsStatusSelector: chatChannelsStatusSelector,
  chatServerTotalUnreadSelector: chatTotalUnreadSelector,
  makeSupportChannelSelector,
  chatServerArchiveCountSelector: chatArchiveCountSelector,
  makeResolvedParticipantSelector,
  makeChannelByIdSelector,
  makeChannelByUserTokenSelector,
  makeChannelIdsSelector,
  makeArchivedChannelIdsSelector,
  makeChannelArchivedSelector,
  makeFavoriteChannelIdsSelector,
  makeChannelUnreadSelector,
  makeParticipantsByChannelSelector,
  makeOtherParticipantSelector,
  makeChannelParticipantTokenSelector,
  makeMessagesByChannelSelector,
  makeMessageByIdSelector,
  makeLastMessageByChannelSelector,
  makeMessageBySidSelector,
  makeReadReceiptSelector,
  makeTypingSelector,
} = createChatServerSelectors(chatStateSelector)

/**
 * Id of the newest outgoing message in a channel — the only bubble that shows
 * the sent/read tick (older markers are noise). Null when the viewer's chat uid
 * is unknown — callers fall back to per-message markers then.
 */
export const makeLastOutgoingMessageIdSelector = (channelId: string | null) =>
  createSelector([makeMessagesByChannelSelector(channelId), profileChatUidSelector], (messages, uid) => {
    if (!uid) return null
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender?.external_id === uid) return messages[i].id
    }
    return null
  })

/**
 * Ids of the newest reactable message on each side (own / peer) — the bubbles that
 * surface the actions trigger by default; others reveal it on focus. Own reactable
 * messages are exactly the ones that also carry edit/delete, so this covers them.
 */
export const makeLastReactableMessageIdsSelector = (channelId: string | null) =>
  createSelector([makeMessagesByChannelSelector(channelId), profileChatUidSelector], (messages, uid) => {
    let outgoing: string | null = null
    let incoming: string | null = null
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (!REACTABLE_MESSAGE_TYPES.includes(message.type) || message.pending || message.rejected || message.cancelled_at) continue
      if (!!uid && message.sender?.external_id === uid) outgoing ??= message.id
      else incoming ??= message.id
      if (outgoing && incoming) break
    }
    return { outgoing, incoming }
  })

/** Mirrors the reactable gate in Message.tsx (server-reactable types only). */
const REACTABLE_MESSAGE_TYPES = ['text', 'image', 'file']
